/* eslint-disable react/prop-types */
import React, { Component } from "react";
// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import LinearProgress from "@material-ui/core/LinearProgress";
import moment from "moment";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// authentication
import { withAxios } from "containers/authenticated.js";

// core components
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import Table from "components/Table/Table.jsx";

import {
  getGoalDetails,
  addGoalMilestones,
  addGoalTasks,
  deleteGoalMilestones,
  deleteGoalTasks
} from "services/goalsDataProvider";
import {
  successColor,
  warningColor,
  dangerColor,
  grayColor,
  whiteColor
} from "assets/jss/material-dashboard-react.jsx";

import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.jsx";

const goalDetailsColors = {
  success: successColor[0],
  warning: warningColor[0],
  danger: dangerColor[0],
  surface: whiteColor,
  mutedSurface: grayColor[13]
};

class GoalDetails extends Component {
  updateTask = async (task, updates = {}) => {
    const { goal } = this.state;
    const { axios } = this.props;
    if (!goal || !task || !task.milestone) return;

    const taskPayload = {
      id: task.id,
      milestone_id: task.milestone,
      name: task.name || "",
      description: task.description || "",
      target_date: task.target_date || "",
      target_value: task.target_value,
      target_type: task.target_type,
      completed: !!task.completed,
      ...updates
    };

    try {
      await addGoalTasks(axios, goal.id, taskPayload.milestone_id, taskPayload);
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({ goal: updatedGoal });
    } catch (error) {
      alert("Failed to update task");
    }
  };

  handleTaskActionChange = task => async event => {
    const action = event.target.value;
    event.target.value = "";

    if (!action) {
      return;
    }

    if (action === "open_details") {
      this.handleViewTask(task);
      return;
    }

    if (action === "move_due_date_today") {
      await this.updateTask(task, {
        target_date: moment().format("YYYY-MM-DD")
      });
      return;
    }
  };

  handleDeleteMilestone = async milestone => {
    if (!window.confirm("Are you sure you want to delete this milestone?")) {
      return;
    }
    const { goal } = this.state;
    const { axios } = this.props;
    try {
      await deleteGoalMilestones(axios, goal.id, milestone.id);
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({
        goal: updatedGoal,
        showMilestoneModal: false,
        viewingMilestone: null,
        editingMilestone: null
      });
    } catch (err) {
      alert("Failed to delete milestone");
    }
  };
  handleUpdateTask = async task => {
    this.setState({ taskSubmitting: true, taskError: null });
    const { taskForm, goal } = this.state;
    const { axios } = this.props;
    try {
      const taskPayload = {
        id: task.id,
        milestone_id: this.state.viewingTaskMilestone,
        name: taskForm.name,
        description: taskForm.description,
        target_date: taskForm.target_date,
        target_value: taskForm.target_value,
        target_type: taskForm.target_type,
        completed: taskForm.completed
      };
      await addGoalTasks(axios, goal.id, taskPayload.milestone_id, taskPayload);
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({
        goal: updatedGoal,
        showTaskModal: false,
        viewingTask: null,
        editingTask: null,
        taskForm: {
          name: "",
          description: "",
          target_date: "",
          target_value: "",
          target_type: "",
          completed: false
        },
        taskSubmitting: false,
        taskError: null
      });
    } catch (err) {
      this.setState({
        taskError: "Failed to update task",
        taskSubmitting: false
      });
    }
  };
  handleEditTask = task => {
    this.setState({
      viewingTask: null,
      editingTask: task,
      showTaskModal: true,
      taskForm: {
        name: task.name || "",
        description: task.description || "",
        target_date: task.target_date || "",
        target_value: task.target_value || "",
        target_type: task.target_type || "",
        completed: task.completed || false
      },
      taskError: null
    });
  };

  handleDeleteTask = async task => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }
    const { goal } = this.state;
    const { axios } = this.props;
    try {
      await deleteGoalTasks(axios, goal.id, task.milestone, task.id);
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({
        goal: updatedGoal,
        showTaskModal: false,
        viewingTask: null,
        editingTask: null
      });
    } catch (err) {
      alert("Failed to delete task");
    }
  };
  handleViewTask = task => {
    this.setState({
      showTaskModal: true,
      viewingTask: task,
      editingTask: null,
      viewingTaskMilestone: task.milestone,
      taskForm: {
        name: task.name || "",
        description: task.description || "",
        target_date: task.target_date || "",
        target_value: task.target_value || "",
        target_type: task.target_type || "",
        completed: task.completed || false
      },
      taskError: null
    });
  };
  handleUpdateMilestone = async milestone => {
    this.setState({ milestoneSubmitting: true, milestoneError: null });
    const { milestoneForm, goal } = this.state;
    const { axios } = this.props;
    try {
      // Call API to update milestone (assuming addGoalMilestones can handle update if id is present)
      const milestonePayload = {
        id: milestone.id,
        name: milestoneForm.name,
        description: milestoneForm.description,
        target_date: milestoneForm.target_date,
        completed: milestoneForm.completed,
        percentage_completion: milestoneForm.percentage_completion
      };
      await addGoalMilestones(axios, goal.id, milestonePayload);
      // Refresh goal details to get updated milestones
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({
        goal: updatedGoal,
        showMilestoneModal: false,
        viewingMilestone: null,
        editingMilestone: null,
        milestoneForm: {
          name: "",
          description: "",
          target_date: "",
          completed: false,
          percentage_completion: 0
        },
        milestoneSubmitting: false,
        milestoneError: null
      });
    } catch (err) {
      this.setState({
        milestoneError: "Failed to update milestone",
        milestoneSubmitting: false
      });
    }
  };

  constructor(props) {
    super(props);
    this.state = {
      goal: null,
      loading: true,
      error: null,
      showMilestoneModal: false,
      milestoneForm: {
        name: "",
        description: "",
        target_date: "",
        completed: false,
        percentage_completion: 0
      },
      milestoneSubmitting: false,
      milestoneError: null,
      expandedMilestones: {
        // milestoneId: true/false
      }
    };
  }

  renderTaskModal() {
    const {
      showTaskModal,
      taskForm,
      taskSubmitting,
      taskError,
      viewingTask,
      editingTask
    } = this.state || {};
    if (!showTaskModal) return null;
    const isViewing = !!viewingTask;
    const isEditing = !!editingTask;
    const isAddingNew = !isViewing && !isEditing;
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.3)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div
          style={{
            background: goalDetailsColors.surface,
            padding: "30px",
            borderRadius: "8px",
            minWidth: "350px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.2)"
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            {isViewing ? "Task Details" : "Add Task"}
          </h3>
          <form onSubmit={this.handleTaskSubmit}>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Name:
                <br />
                <input
                  type="text"
                  name="name"
                  value={taskForm.name}
                  onChange={this.handleTaskFormChange}
                  required
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Description:
                <br />
                <textarea
                  name="description"
                  value={taskForm.description}
                  onChange={this.handleTaskFormChange}
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Target Date:
                <br />
                <input
                  type="date"
                  name="target_date"
                  value={taskForm.target_date}
                  onChange={this.handleTaskFormChange}
                  required
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Target Value:
                <br />
                <input
                  type="number"
                  name="target_value"
                  value={taskForm.target_value}
                  onChange={this.handleTaskFormChange}
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Target Type:
                <br />
                <input
                  type="text"
                  name="target_type"
                  value={taskForm.target_type}
                  onChange={this.handleTaskFormChange}
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Completed:
                <input
                  type="checkbox"
                  name="completed"
                  checked={taskForm.completed}
                  onChange={this.handleTaskFormChange}
                  style={{ marginLeft: "10px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            {taskError && (
              <div
                style={{ color: goalDetailsColors.danger, marginBottom: "10px" }}
              >
                {taskError}
              </div>
            )}
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                justIcon
                color="info"
                size="sm"
                onClick={this.handleTaskModalClose}
                disabled={taskSubmitting}
                title="Close"
              >
                <Icon>close</Icon>
              </Button>
              {isViewing && (
                <Button
                  justIcon
                  color="warning"
                  size="sm"
                  title="Edit"
                  onClick={() => this.handleEditTask(viewingTask)}
                >
                  <Icon>edit</Icon>
                </Button>
              )}
              {isViewing && (
                <Button
                  justIcon
                  color="danger"
                  size="sm"
                  title="Delete"
                  onClick={() => this.handleDeleteTask(viewingTask)}
                >
                  <Icon>delete</Icon>
                </Button>
              )}
              {isEditing && (
                <Button
                  justIcon
                  color="success"
                  size="sm"
                  title="Update"
                  onClick={() => this.handleUpdateTask(editingTask)}
                >
                  <Icon>check</Icon>
                </Button>
              )}
              {isAddingNew && (
                <Button
                  justIcon
                  color="success"
                  size="sm"
                  title="Add"
                  onClick={this.handleTaskSubmit}
                >
                  <Icon>add</Icon>
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }
  handleAddTask = milestoneId => () => {
    this.setState({
      showTaskModal: true,
      addTaskForMilestone: milestoneId,
      taskError: null,
      taskForm: {
        name: "",
        description: "",
        target_date: "",
        target_value: "",
        target_type: "",
        completed: false
      },
      taskSubmitting: false
    });
  };

  handleTaskFormChange = e => {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => ({
      taskForm: {
        ...prevState.taskForm,
        [name]: type === "checkbox" ? checked : value
      }
    }));
  };

  handleTaskModalClose = () => {
    this.setState({
      showTaskModal: false,
      addTaskForMilestone: null,
      taskError: null,
      taskForm: {
        name: "",
        description: "",
        target_date: "",
        target_value: "",
        target_type: "",
        completed: false
      }
    });
  };

  handleTaskSubmit = async e => {
    e.preventDefault();
    this.setState({ taskSubmitting: true, taskError: null });
    const { taskForm, goal } = this.state;
    const { axios } = this.props;
    try {
      // Call API to add task
      const taskPayload = {
        milestone_id: this.state.addTaskForMilestone,
        name: taskForm.name,
        description: taskForm.description,
        target_date: taskForm.target_date,
        target_value: taskForm.target_value,
        target_type: taskForm.target_type,
        completed: taskForm.completed
      };
      await addGoalTasks(axios, goal.id, taskPayload.milestone_id, taskPayload);
      // Refresh goal details to get updated tasks
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({
        goal: updatedGoal,
        showTaskModal: false,
        taskForm: {
          name: "",
          description: "",
          target_date: "",
          target_value: "",
          target_type: "",
          completed: false
        },
        taskSubmitting: false,
        taskError: null
      });
    } catch (err) {
      this.setState({ taskError: "Failed to add task", taskSubmitting: false });
    }
  };

  handleViewMilestone = milestone => {
    this.setState({
      showMilestoneModal: true,
      viewingMilestone: milestone,
      milestoneForm: {
        name: milestone.name || "",
        description: milestone.description || "",
        target_date: milestone.target_date || "",
        completed: milestone.completed || false,
        percentage_completion: milestone.percentage_completion || 0
      },
      milestoneError: null
    });
  };

  handleEditMilestone = milestone => {
    this.setState({
      viewingMilestone: null,
      editingMilestone: milestone,
      showMilestoneModal: true,
      milestoneForm: {
        name: milestone.name || "",
        description: milestone.description || "",
        target_date: milestone.target_date || "",
        completed: milestone.completed || false,
        percentage_completion: milestone.percentage_completion || 0
      },
      milestoneError: null
    });
  };

  renderMilestoneModal() {
    const {
      showMilestoneModal,
      milestoneForm,
      milestoneSubmitting,
      milestoneError,
      viewingMilestone,
      editingMilestone
    } = this.state;
    if (!showMilestoneModal) return null;
    const isViewing = !!viewingMilestone;
    const isEditing = !!editingMilestone;
    const isAddingNew = !isViewing && !isEditing;
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.3)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <div
          style={{
            background: goalDetailsColors.surface,
            padding: "30px",
            borderRadius: "8px",
            minWidth: "350px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.2)"
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            {isViewing ? "Milestone Details" : "Add Milestone"}
          </h3>
          <form onSubmit={this.handleMilestoneSubmit}>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Name:
                <br />
                <input
                  type="text"
                  name="name"
                  value={milestoneForm.name}
                  onChange={this.handleMilestoneFormChange}
                  required
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Description:
                <br />
                <textarea
                  name="description"
                  value={milestoneForm.description}
                  onChange={this.handleMilestoneFormChange}
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Target Date:
                <br />
                <input
                  type="date"
                  name="target_date"
                  value={milestoneForm.target_date}
                  onChange={this.handleMilestoneFormChange}
                  required
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Completed:
                <input
                  type="checkbox"
                  name="completed"
                  checked={milestoneForm.completed}
                  onChange={this.handleMilestoneFormChange}
                  style={{ marginLeft: "10px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            <div style={{ marginBottom: "15px" }}>
              <label>
                Completion Percentage:
                <br />
                <input
                  type="number"
                  name="percentage_completion"
                  value={milestoneForm.percentage_completion}
                  min={0}
                  max={100}
                  onChange={this.handleMilestoneFormChange}
                  required
                  style={{ width: "100%", padding: "8px" }}
                  disabled={isViewing}
                />
              </label>
            </div>
            {milestoneError && (
              <div
                style={{ color: goalDetailsColors.danger, marginBottom: "10px" }}
              >
                {milestoneError}
              </div>
            )}
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                justIcon
                color="info"
                size="sm"
                onClick={this.handleMilestoneModalClose}
                disabled={milestoneSubmitting}
                title="Close"
              >
                <Icon>close</Icon>
              </Button>
              {isViewing && (
                <React.Fragment>
                  <Button
                    justIcon
                    color="warning"
                    size="sm"
                    title="Edit"
                    onClick={() => this.handleEditMilestone(viewingMilestone)}
                  >
                    <Icon>edit</Icon>
                  </Button>
                  <Button
                    justIcon
                    color="danger"
                    size="sm"
                    title="Delete"
                    onClick={() => this.handleDeleteMilestone(viewingMilestone)}
                  >
                    <Icon>delete</Icon>
                  </Button>
                </React.Fragment>
              )}
              {isEditing && (
                <React.Fragment>
                  <Button
                    justIcon
                    color="success"
                    size="sm"
                    title="Update"
                    onClick={() => this.handleUpdateMilestone(editingMilestone)}
                  >
                    <Icon>check</Icon>
                  </Button>
                </React.Fragment>
              )}
              {isAddingNew && (
                <React.Fragment>
                  <Button
                    justIcon
                    color="success"
                    size="sm"
                    title="Add"
                    onClick={this.handleMilestoneSubmit}
                  >
                    <Icon>add</Icon>
                  </Button>
                </React.Fragment>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  }

  handleAddMilestone = () => {
    this.setState({ showMilestoneModal: true, milestoneError: null });
  };

  handleMilestoneFormChange = e => {
    const { name, value, type, checked } = e.target;
    this.setState(prevState => ({
      milestoneForm: {
        ...prevState.milestoneForm,
        [name]: type === "checkbox" ? checked : value
      }
    }));
  };

  handleMilestoneModalClose = () => {
    this.setState({
      showMilestoneModal: false,
      viewingMilestone: null,
      editingMilestone: null,
      milestoneForm: {
        name: "",
        description: "",
        target_date: "",
        completed: false,
        percentage_completion: 0
      },
      milestoneError: null
    });
  };

  handleMilestoneSubmit = async e => {
    e.preventDefault();
    this.setState({ milestoneSubmitting: true, milestoneError: null });
    const { milestoneForm, goal } = this.state;
    const { axios } = this.props;
    try {
      // Call API to add milestone
      const milestonePayload = {
        name: milestoneForm.name,
        description: milestoneForm.description,
        target_date: milestoneForm.target_date,
        completed: milestoneForm.completed,
        percentage_completion: milestoneForm.percentage_completion
      };
      await addGoalMilestones(axios, goal.id, milestonePayload);
      // Refresh goal details to get updated milestones
      const updatedGoal = await getGoalDetails(axios, goal.id);
      this.setState({
        goal: updatedGoal,
        showMilestoneModal: false,
        milestoneForm: {
          name: "",
          description: "",
          target_date: "",
          completed: false,
          percentage_completion: 0
        },
        milestoneSubmitting: false,
        milestoneError: null
      });
    } catch (err) {
      this.setState({
        milestoneError: "Failed to add milestone",
        milestoneSubmitting: false
      });
    }
  };

  handleToggleMilestoneExpand = milestoneId => {
    this.setState(prevState => ({
      expandedMilestones: {
        ...prevState.expandedMilestones,
        [milestoneId]: !prevState.expandedMilestones[milestoneId]
      }
    }));
  };

  getTaskFromHash = goalData => {
    if (typeof window === "undefined" || !window.location || !goalData) {
      return null;
    }

    const hash = window.location.hash || "";
    const match = hash.match(/^#task-(.+)$/);
    if (!match) {
      return null;
    }

    const targetTaskId = String(match[1]);
    const milestones = goalData.milestones || [];
    for (let i = 0; i < milestones.length; i += 1) {
      const milestone = milestones[i];
      const tasks = milestone.tasks || [];
      const matchedTask = tasks.find(task => String(task.id) === targetTaskId);
      if (matchedTask) {
        return {
          task: {
            ...matchedTask,
            milestone: matchedTask.milestone || milestone.id
          },
          milestoneId: milestone.id
        };
      }
    }

    return null;
  };

  componentDidMount() {
    this.loadGoalDetails();
  }

  loadGoalDetails = async () => {
    // Get goalId from URL params if not provided via props
    const goalId =
      this.props.goalId ||
      (this.props.match &&
        this.props.match.params &&
        this.props.match.params.goalId);
    console.log("Loading details for goalId:", goalId);
    if (!goalId) {
      this.props.history.push("/my-goals");
      return;
    }
    try {
      this.setState({ loading: true, goalId: goalId });
      const goalData = await getGoalDetails(this.props.axios, goalId);
      const taskFromHash = this.getTaskFromHash(goalData);

      if (taskFromHash) {
        this.setState(
          prevState => ({
            goal: goalData,
            loading: false,
            expandedMilestones: {
              ...prevState.expandedMilestones,
              [taskFromHash.milestoneId]: true
            }
          }),
          () => {
            this.handleViewTask(taskFromHash.task);
          }
        );
      } else {
        this.setState({ goal: goalData, loading: false });
      }
    } catch (error) {
      console.error("Failed to load goal details:", error);
      this.setState({ error: "Failed to load goal details", loading: false });
    }
  };

  handleBackClick = () => {
    this.props.history.push("/my-goals");
  };

  render() {
    // const { classes } = this.props;
    const { goal, loading, error } = this.state;

    if (loading) {
      return <div>Loading goal details...</div>;
    }

    if (error) {
      return (
        <div>
          <p>{error}</p>
          <Button color="info" onClick={this.handleBackClick}>
            <ArrowBackIcon style={{ marginRight: "8px" }} />
            Back to Goals
          </Button>
        </div>
      );
    }

    if (!goal) {
      return <div>Goal not found</div>;
    }

    const daysLeft = moment(goal.target_date).diff(moment(), "days");
    const localizedTargetDate = moment(goal.target_date).format("LL");
    const percentageCompletion =
      (goal.metadata && goal.metadata.percentage_completion) || 0;
    const currentValue = (goal.metadata && goal.metadata.current_value) || 0;
    const tasks = (goal.milestones || []).reduce((allTasks, milestone) => {
      const milestoneTasks = (milestone && milestone.tasks) || [];
      return allTasks.concat(milestoneTasks);
    }, []);
    const completedTaskCount = tasks.filter(task => task.completed).length;
    const openTaskCount = tasks.length - completedTaskCount;

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div style={{ marginBottom: "20px" }}>
            <Button color="info" onClick={this.handleBackClick}>
              <ArrowBackIcon style={{ marginRight: "8px" }} />
              Back to Goals
            </Button>
          </div>
          <Card>
            <CardHeader color="info" stats icon>
              <CardIcon color="info">
                <Icon>flag</Icon>
              </CardIcon>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}
              >
                <div>
                  <h3 style={{ margin: 0 }}>{goal.name}</h3>
                  <p style={{ margin: 0, opacity: 0.8 }}>
                    {goal.goal_type} Goal
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} md={6}>
                  <h4>Goal Information</h4>
                  <p>
                    <strong>Description:</strong>{" "}
                    {goal.description || "No description provided"}
                  </p>
                  <p>
                    <strong>Target Date:</strong> {localizedTargetDate}
                  </p>
                  <p>
                    <strong>Target Value:</strong> {goal.target_value}{" "}
                    {goal.target_metric}
                  </p>
                  <p>
                    <strong>Current Progress:</strong> {currentValue}{" "}
                    {goal.target_metric}
                  </p>
                  <p>
                    <strong>Status:</strong>{" "}
                    {goal.completed ? "Completed" : "In Progress"}
                  </p>
                  <p>
                    <strong>Shared Goal:</strong>{" "}
                    {goal.is_shared ? "Yes" : "No"}
                  </p>
                  <p>
                    <strong>Public Goal:</strong>{" "}
                    {goal.is_public ? "Yes" : "No"}
                  </p>
                </GridItem>
                <GridItem xs={12} md={6}>
                  <h4>Progress Overview</h4>
                  <div style={{ marginBottom: "20px" }}>
                    <p>
                      <strong>Completion:</strong>{" "}
                      {percentageCompletion.toFixed(1)}%
                    </p>
                    <LinearProgress
                      variant="determinate"
                      value={percentageCompletion}
                      style={{ height: "10px", borderRadius: "5px" }}
                    />
                  </div>
                  <div style={{ marginBottom: "20px" }}>
                    <p>
                      <strong>Time Remaining:</strong>
                    </p>
                    <p
                      style={{
                        fontSize: "1.2rem",
                        color:
                          daysLeft > 0
                            ? goalDetailsColors.success
                            : goalDetailsColors.danger
                      }}
                    >
                      {daysLeft > 0
                        ? `${daysLeft} days left`
                        : "Target date passed"}
                    </p>
                  </div>
                  <div style={{ marginBottom: "20px" }}>
                    <p>
                      <strong>Tasks:</strong> {completedTaskCount} completed /{" "}
                      {openTaskCount} open
                    </p>
                  </div>
                </GridItem>
              </GridContainer>

              {goal.metadata &&
                Object.keys(goal.metadata).length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    <h4>Additional Information</h4>
                    <pre
                      style={{
                        backgroundColor: goalDetailsColors.mutedSurface,
                        padding: "10px",
                        borderRadius: "4px",
                        overflow: "auto"
                      }}
                    >
                      {JSON.stringify(goal.metadata, null, 2)}
                    </pre>
                  </div>
                )}

              {/* Milestone Modal */}
              {this.renderMilestoneModal()}
              {/* Labeled action button is clearer than an icon-only trigger */}
              <Button
                color="primary"
                onClick={this.handleAddMilestone}
                style={{ marginTop: "20px", float: "right" }}
                title="Add Milestone"
              >
                <Icon style={{ marginRight: "6px" }}>add</Icon>
                Add Milestone
              </Button>

              {/* Task Modal */}
              {this.renderTaskModal()}

              {/* Milestones & Tasks Table */}
              {goal.milestones &&
                goal.milestones.length > 0 && (
                  <div style={{ marginTop: "30px" }}>
                    <h4>Milestones & Tasks</h4>
                    <Table
                      tableHeaderColor="info"
                      tableHead={[
                        "",
                        "Milestone/Task",
                        "Target Value",
                        "Target Date",
                        "Status",
                        "Actions"
                      ]}
                      tableData={(() => {
                        const rows = [];
                        goal.milestones.forEach(milestone => {
                          const isExpanded = this.state.expandedMilestones[
                            milestone.id
                          ];
                          rows.push([
                            <Icon
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                this.handleToggleMilestoneExpand(milestone.id)
                              }
                              key={`expand-${milestone.id}`}
                            >
                              {isExpanded ? "expand_less" : "expand_more"}
                            </Icon>,
                            <strong key={milestone.id}>
                              {milestone.name}
                            </strong>,
                            milestone.target_value
                              ? `${
                                  milestone.target_value
                                } ${milestone.target_type ||
                                  goal.target_metric}`
                              : "N/A",
                            milestone.target_date
                              ? moment(milestone.target_date).format("LL")
                              : "No date set",
                            milestone.completed ? (
                              <span
                                key={`status-${milestone.id}`}
                                style={{
                                  color: goalDetailsColors.success,
                                  fontWeight: "bold"
                                }}
                              >
                                ✓ Completed
                              </span>
                            ) : (
                              <span
                                key={`status-${milestone.id}`}
                                style={{
                                  color: goalDetailsColors.warning,
                                  fontWeight: "bold"
                                }}
                              >
                                In Progress
                              </span>
                            ),
                            /*Add icon buttons for: edit, delete, add-task*/
                            <div
                              key={`actions-${milestone.id}`}
                              style={{ display: "flex", gap: "4px" }}
                            >
                              <Button
                                justIcon
                                color="info"
                                size="sm"
                                key={`view-${milestone.id}`}
                                title="View Milestone"
                                onClick={() =>
                                  this.handleViewMilestone(milestone)
                                }
                              >
                                <Icon>visibility</Icon>
                              </Button>
                              <Button
                                justIcon
                                color="primary"
                                size="sm"
                                key={`add-task-${milestone.id}`}
                                title="Add Task"
                                onClick={this.handleAddTask(milestone.id)}
                              >
                                <Icon>add</Icon>
                              </Button>
                            </div>
                          ]);
                          if (
                            isExpanded &&
                            milestone.tasks &&
                            milestone.tasks.length > 0
                          ) {
                            milestone.tasks.forEach(task => {
                              rows.push([
                                "",
                                <span
                                  key={task.id || task.name}
                                  style={{ paddingLeft: 32 }}
                                >
                                  {task.name}
                                </span>,
                                task.target_value
                                  ? `${task.target_value} ${task.target_type ||
                                      goal.target_metric}`
                                  : "N/A",
                                task.target_date
                                  ? moment(task.target_date).format("LL")
                                  : "No date set",
                                task.completed ? (
                                  <span
                                    key={`status-task-${task.id || task.name}`}
                                    style={{
                                      color: goalDetailsColors.success,
                                      fontWeight: "bold"
                                    }}
                                  >
                                    ✓ Completed
                                  </span>
                                ) : (
                                  <span
                                    key={`status-task-${task.id || task.name}`}
                                    style={{
                                      color: goalDetailsColors.warning,
                                      fontWeight: "bold"
                                    }}
                                  >
                                    In Progress
                                  </span>
                                ),
                                <div
                                  key={`actions-task-${task.id || task.name}`}
                                  style={{ display: "flex", gap: "4px" }}
                                >
                                  <select
                                    defaultValue=""
                                    onChange={this.handleTaskActionChange(task)}
                                    style={{ height: "30px", fontSize: "12px" }}
                                    title="Actions"
                                  >
                                    <option value="" disabled>
                                      Actions
                                    </option>
                                    <option value="open_details">
                                      Open details
                                    </option>
                                    <option value="move_due_date_today">
                                      Move due date to today
                                    </option>
                                  </select>
                                </div>
                              ]);
                            });
                          }
                        });
                        return rows;
                      })()}
                    />
                  </div>
                )}
            </CardBody>

            <CardFooter stats>
              <div style={{ display: "flex", gap: "10px" }}>
                {!goal.completed && (
                  <Button color="success">Mark as Complete</Button>
                )}
              </div>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

export default withAxios(withStyles(styles)(GoalDetails));
